import { useMemo } from 'react';
import { AlertCircle, Check, Clock, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type EphemeralToolCallStatus =
  | 'invoking'
  | 'awaiting_execution'
  | 'running'
  | 'completed'
  | 'failed'
  | 'pending_approval';

export interface EphemeralToolCall {
  toolCallId: string;
  name: string;
  status: EphemeralToolCallStatus;
}

export type EphemeralPhase = 'streaming' | 'cancelled' | 'hidden';

export interface EphemeralThinkingSurfaceProps {
  phase: EphemeralPhase;
  streamingText: string;
  toolCalls: EphemeralToolCall[];
  onCancel: () => void;
  onRetry: () => void;
}

function StatusIcon({ status }: { status: EphemeralToolCallStatus }) {
  if (status === 'invoking' || status === 'awaiting_execution') {
    return <Loader2 className="h-3 w-3 animate-spin text-warning" />;
  }
  if (status === 'running') {
    return <Loader2 className="h-3 w-3 animate-spin text-info" />;
  }
  if (status === 'completed') {
    return <Check className="h-3 w-3 text-success" />;
  }
  if (status === 'failed') {
    return <AlertCircle className="h-3 w-3 text-destructive" />;
  }
  return <Clock className="h-3 w-3 text-warning" />;
}

function parseStreamingHeader(text: string): string | null {
  const trimmed = text.trimStart();
  if (trimmed.length === 0) return null;
  const newlineIdx = trimmed.indexOf('\n');
  const firstLine = newlineIdx === -1 ? trimmed : trimmed.slice(0, newlineIdx);
  const match = firstLine.match(/^\s*\*([^*]+)\*\s*$/);
  return match ? match[1].trim() : null;
}

export function EphemeralThinkingSurface({
  phase,
  streamingText,
  toolCalls,
  onCancel,
  onRetry,
}: EphemeralThinkingSurfaceProps) {
  const headerTitle = useMemo(() => parseStreamingHeader(streamingText), [streamingText]);

  if (phase === 'hidden') {
    return null;
  }

  return (
    <div
      data-testid="speak2me-ephemeral-thinking"
      className="relative border-l-2 border-muted pl-3 py-2 my-2 text-muted-foreground text-sm"
    >
      {toolCalls.length > 0 ? (
        <div className="flex flex-wrap items-center pr-20">
          {toolCalls.map((tc) => (
            <span
              key={tc.toolCallId}
              className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 px-2 py-0.5 text-xs mr-1.5 mb-1"
            >
              <StatusIcon status={tc.status} />
              <span className="font-mono text-[0.7rem]">{tc.name}</span>
            </span>
          ))}
        </div>
      ) : null}

      {phase === 'streaming' ? (
        <p className="italic leading-snug mt-1 inline-flex items-center gap-2 pr-20">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>{headerTitle ?? 'Thinking…'}</span>
        </p>
      ) : null}

      {phase === 'cancelled' ? (
        <p className="italic leading-snug mt-1 pr-20">Turn cancelled.</p>
      ) : null}

      <div className="absolute right-2 top-2">
        {phase === 'streaming' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            data-testid="speak2me-cancel-turn"
          >
            <X className="h-3 w-3 mr-1" />
            Cancel
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            data-testid="speak2me-retry-turn"
          >
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}
